// Options: sound, music and animation choices. Saved in this browser only.
import { useSettings, updateSettings, resetSettings, type Motion } from '../fx/settings.ts';
import { play, SOUND_NAMES, isUnlocked, unlockAudio } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';
import { enableDesktopAlerts, desktopAlertsSupported } from '../fx/alerts.ts';
import { Panel } from '../components/ui.tsx';
import { useGame } from '../state.tsx';

const SOUND_LABELS: Record<string, string> = {
  click: 'Button click', tab: 'Tab', coin: 'Job cash', coins: 'Coins', chaching: 'Collect income', buy: 'Buy', error: 'Not allowed',
  chime: 'Mastery', fanfare: 'Level up', medal: 'Achievement', punch: 'Punch', gunshot: 'Gunshot', cheer: 'Crowd cheers',
  groan: 'Defeat', bossHit: 'Boss hit', roar: 'Boss roar', deal: 'Deal a card', flip: 'Flip a card', chips: 'Chips',
  chipSlide: 'Pot to the winner', spin: 'Slot reels', reelStop: 'Reel stops', win: 'Win', bigWin: 'Big win', jackpot: 'JACKPOT',
  lose: 'Lose', ball: 'Roulette ball', knock: 'Your turn (poker)', bugle: 'Call to the post', hooves: 'Hooves', drum: 'War drums',
  horn: 'Victory horn', pop: 'Chat message', mention: '@mention', alert: 'Something happened to you', like: 'Like', heal: 'Heal',
  munch: 'Pastrami Sandwich', whoosh: 'Whoosh', swords: 'Turf battle', stamp: 'Rubber stamp', envelope: 'Envelope', bell: 'Bell',
  typewriter: 'Typewriter', siren: 'Siege alarm', chest: 'Reward chest',
  gavel: 'Auction gavel', streak: 'Winning streak', handshake: 'Trade done',
};

function Slider({ label, value, onChange, disabled }: { label: string; value: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <label className={`opt-row ${disabled ? 'dim' : ''}`}>
      <span className="opt-label">{label}</span>
      <input type="range" min={0} max={100} value={Math.round(value * 100)} disabled={disabled} onChange={(e) => onChange(Number(e.target.value) / 100)} aria-label={label} />
      <span className="opt-val">{Math.round(value * 100)}%</span>
    </label>
  );
}

function Toggle({ label, help, value, onChange }: { label: string; help?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="opt-row opt-toggle">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="opt-label"><b>{label}</b>{help && <div className="small muted">{help}</div>}</span>
    </label>
  );
}

export function OptionsPage() {
  const s = useSettings();
  const { setFlash } = useGame();
  const test = (name: string, opts?: { duration?: number }) => {
    if (!isUnlocked()) unlockAudio();
    play(name, opts);
  };
  return (
    <div>
      <h1 className="page-title">Options</h1>
      <p className="muted">These settings are saved in this browser only, so you can have sound on at home and off on your phone.</p>

      <Panel title="Sound">
        <Toggle label="Mute everything" help="Same as the speaker button in the top bar." value={s.muted} onChange={(v) => updateSettings({ muted: v })} />
        <Slider label="Main volume" value={s.master} onChange={(v) => updateSettings({ master: v })} disabled={s.muted} />
        <Toggle label="Sound effects" help="Cash, fights, cards, slots, level-ups and more." value={s.sfx} onChange={(v) => updateSettings({ sfx: v })} />
        <Slider label="Effects volume" value={s.sfxVolume} onChange={(v) => { updateSettings({ sfxVolume: v }); play('coin'); }} disabled={!s.sfx || s.muted} />
        <Toggle label="Button clicks" help="A soft click on buttons and tabs." value={s.clicks} onChange={(v) => updateSettings({ clicks: v })} />
        <Toggle label="Alert sounds" help="New chat messages, @mentions, sieges, your turn at poker, and when someone attacks, robs or messages you." value={s.alerts} onChange={(v) => updateSettings({ alerts: v })} />
      </Panel>

      <Panel title="Alerts">
        <p className="small muted" style={{ marginTop: 0 }}>
          While the game is open in a tab, its title and icon change when something needs you: <b>⚡ Full</b> when your energy refills, a siege on your turf,
          your turn at poker, or the race starting in 15 minutes. These work in any tab, even in the background.
        </p>
        {desktopAlertsSupported() ? (
          <Toggle label="Desktop pop-ups" help="Also show a pop-up from your computer when the game is in a background tab. Your browser will ask for permission."
            value={s.desktopAlerts} onChange={async (v) => {
              if (!v) return updateSettings({ desktopAlerts: false });
              const ok = await enableDesktopAlerts();
              updateSettings({ desktopAlerts: ok });
              if (!ok) setFlash({ kind: 'bad', text: 'Pop-ups are blocked for this site. Allow notifications in your browser’s site settings, then try again.' });
            }} />
        ) : <div className="small dim">This browser can’t show desktop pop-ups.</div>}
      </Panel>

      <Panel title="Music">
        <Toggle label="Background music" help="A quiet lounge-jazz loop, made by the game as it plays (it never repeats exactly)." value={s.music} onChange={(v) => updateSettings({ music: v })} />
        <Slider label="Music volume" value={s.musicVolume} onChange={(v) => updateSettings({ musicVolume: v })} disabled={!s.music || s.muted} />
      </Panel>

      <Panel title="Animations">
        <div className="opt-row opt-radio" role="radiogroup" aria-label="Animations">
          {([['full', 'Full', 'Everything moves: shakes, flashes, spinning reels.'], ['reduced', 'Reduced', 'Gentle fades only; no shaking or flashing.'], ['off', 'Off', 'No animations at all.']] as [Motion, string, string][]).map(([v, label, help]) => (
            <label key={v} className={s.motion === v ? 'on' : ''}>
              <input type="radio" name="motion" checked={s.motion === v} onChange={() => updateSettings({ motion: v })} />
              <b>{label}</b>
              <div className="small muted">{help}</div>
            </label>
          ))}
        </div>
        <Toggle label="Count numbers up" help="Cash and XP roll up to their new values instead of jumping." value={s.countUp} onChange={(v) => updateSettings({ countUp: v })} />
        <div style={{ marginTop: 6 }}>
          <button className="btn small" onClick={() => { fx.levelUp(42); test('fanfare'); }}>Preview level-up</button>{' '}
          <button className="btn small" onClick={() => { fx.coins(18); fx.float('+$1,000,000', 'cash'); test('jackpot'); }}>Preview jackpot</button>
        </div>
      </Panel>

      <Panel title="Try the sounds">
        <div className="sound-grid">
          {SOUND_NAMES.map((n) => (
            <button key={n} className="btn small gray" onClick={() => test(n, { duration: n === 'hooves' ? 3 : n === 'ball' ? 3 : 1.5 })}>{SOUND_LABELS[n] ?? n}</button>
          ))}
        </div>
      </Panel>

      <div className="right"><button className="btn gray" onClick={resetSettings}>Reset to defaults</button></div>
    </div>
  );
}

/** Speaker button for the top bar: one click to mute or unmute everything. */
export function SpeakerButton() {
  const s = useSettings();
  const silent = s.muted || (!s.sfx && !s.music) || s.master === 0;
  return (
    <a className="speaker" title={silent ? 'Sound is off: click to turn it on' : 'Click to mute (more in Options)'} aria-label={silent ? 'Unmute' : 'Mute'}
      onClick={() => {
        if (silent) updateSettings({ muted: false, ...(s.master === 0 ? { master: 0.8 } : {}), ...(!s.sfx && !s.music ? { sfx: true, music: true } : {}) });
        else updateSettings({ muted: true });
      }}>
      <svg width="18" height="16" viewBox="0 0 18 16" aria-hidden="true" style={{ verticalAlign: 'middle' }}>
        <path d="M1 5 h4 l5 -4 v14 l-5 -4 h-4z" fill="currentColor" />
        {silent ? <path d="M12 5 l5 6 M17 5 l-5 6" stroke="currentColor" strokeWidth="1.8" /> : <path d="M12.5 4.5 q2.5 3.5 0 7 M14.5 2.5 q4.5 5.5 0 11" stroke="currentColor" strokeWidth="1.5" fill="none" />}
      </svg>
    </a>
  );
}
